import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';

/**
 * A family member's VIP card, opened from the QR their parent shows them.
 *
 * The backend already renders this page at /family/:token, but Apache only
 * proxies /api/ to Node — every other path falls through to this SPA. So a
 * family member opening their link got the admin site instead of their card,
 * and no amount of fixing the backend page could reach them.
 *
 * Rendering it here means the links already handed out keep working without an
 * Apache change. If /family/ is ever proxied to Node, that page takes over and
 * this becomes dead weight worth deleting.
 *
 * Public by design: the token in the URL is the credential, exactly as it is
 * for the backend page.
 */

const wrap = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 20,
  background: 'linear-gradient(160deg,#0B2545 0%,#13385F 55%,#0B2545 100%)',
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
};

const card = {
  width: '100%',
  maxWidth: 380,
  background: '#fff',
  borderRadius: 22,
  padding: 26,
  textAlign: 'center',
  boxShadow: '0 18px 48px rgba(0,0,0,0.35)',
};

export default function FamilyCardPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      // Plain fetch, not the admin axios client: this page is public and must
      // not carry or require an admin token.
      const r = await fetch(`/api/family/qr/${token}`, { cache: 'no-store' });
      const d = await r.json();
      if (!r.ok) { setError(d.message || 'This card link is not valid.'); return; }
      setError('');
      setData(d);
    } catch {
      setError('Could not load this card. Check your connection and try again.');
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // The code rolls hourly; refresh just after the boundary so a card left open
  // at a counter still scans.
  useEffect(() => {
    if (!data?.refreshAt) return undefined;
    const ms = new Date(data.refreshAt).getTime() - Date.now();
    if (ms <= 0 || ms > 3700000) return undefined;
    const t = setTimeout(load, ms + 1000);
    return () => clearTimeout(t);
  }, [data, load]);

  if (error) {
    return (
      <div style={wrap}>
        <div style={card}>
          <div style={{ fontSize: 15, color: '#B3261E', lineHeight: 1.5 }}>{error}</div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={wrap}>
        <div style={{ ...card, color: '#667' }}>Loading your card…</div>
      </div>
    );
  }

  const expiry = data.expiryDate
    ? new Date(data.expiryDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

  return (
    <div style={wrap}>
      <div style={card}>
        <div style={{
          display: 'inline-block', background: '#EEF6FD', color: '#0F559D',
          borderRadius: 999, padding: '5px 14px', fontSize: 11.5, fontWeight: 800,
          letterSpacing: 1, textTransform: 'uppercase',
        }}>
          Family VIP Card
        </div>

        <div style={{ fontSize: 26, fontWeight: 800, marginTop: 14, color: '#10243A' }}>{data.name}</div>
        <div style={{ fontSize: 13, color: '#7A8794', textTransform: 'capitalize' }}>{data.relationship}</div>
        <div style={{ fontSize: 13, color: '#44505C', marginTop: 8 }}>
          On <strong>{data.vipMember}</strong>&rsquo;s membership
        </div>

        <div style={{
          margin: '20px auto 14px', padding: 12, width: 244,
          border: '1px solid #EDF1F5', borderRadius: 16, background: '#fff',
        }}>
          {data.qrDataUri
            ? <img src={data.qrDataUri} width={220} height={220} alt="Family VIP QR code" />
            : (
              <div style={{ width: 220, height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8A96A3', fontSize: 13, padding: 16, textAlign: 'center' }}>
                Your card is being issued. Show your phone number at the till in the meantime.
              </div>
            )}
        </div>

        {/* The card number Business Central knows — what the QR resolves to. */}
        <div style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 13, color: '#5A6672', letterSpacing: 1 }}>
          {(data.memberCardNo || data.memberCode) + ' / F' + data.slot}
        </div>

        <div style={{ fontSize: 12.5, color: '#7A8794', marginTop: 6 }}>Valid until {expiry}</div>

        <div style={{
          marginTop: 16, background: '#0F559D', color: '#fff', borderRadius: 12,
          padding: '11px 14px', fontWeight: 800, letterSpacing: 0.5,
        }}>
          25% OFF ALL TOYS
        </div>

        <div style={{ fontSize: 11.5, color: '#9AA5B0', marginTop: 12, lineHeight: 1.5 }}>
          Show this code at the till. It refreshes every hour for security —
          keep this page open or reopen the link when you shop.
        </div>
      </div>
    </div>
  );
}
